import { Text, Modal, Stack, Group, Code, Tabs, Alert } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconBug, IconBrowser, IconPackage, IconServer } from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import React from 'react';
import packageJson from '../../../package.json';
import { useSystemInfo } from '../../hooks/useSystemInfo';
import { pb } from '../../lib/pocketbase';

function formatBytes(bytes: number): string {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round(bytes / Math.pow(1024, i))} ${sizes[i]}`;
}

export function VersionInfo() {
  const [opened, { open, close }] = useDisclosure(false);
  const { systemInfo, loading, error } = useSystemInfo();
  const [pocketbaseServerVersion, setPocketbaseServerVersion] = useState<string>('Loading...');

  // Fetch PocketBase server version dynamically
  useEffect(() => {
    const fetchPocketBaseVersion = async () => {
      try {
        // Try to fetch version from custom API endpoint
        const response = await fetch(`${pb.baseUrl}/api/version`);
        if (response.ok) {
          const data = await response.json();
          setPocketbaseServerVersion(data.version || 'Unknown');
          return;
        }
      } catch (err) {
        console.log('Custom version endpoint not available, trying fallback');
      }
      
      try {
        // Fallback: Use environment variable
        const envVersion = process.env.REACT_APP_POCKETBASE_VERSION;
        if (envVersion) {
          setPocketbaseServerVersion(envVersion);
          return;
        }
        
        // Last fallback: Check if server is reachable
        await pb.health.check();
        setPocketbaseServerVersion('Connected (version unknown)');
      } catch (err) {
        console.error('Error checking PocketBase connection:', err);
        setPocketbaseServerVersion('Unable to connect');
      }
    };

    if (opened) {
      fetchPocketBaseVersion();
    }
  }, [opened]);

  const debugInfo = {
    version: 'MAY@11022026',
    nodeEnv: process.env.NODE_ENV,
    reactVersion: React.version, // Get actual React version dynamically
    mantineVersion: (packageJson.devDependencies as Record<string, string>)?.['@mantine/core'] ||
                    (packageJson.dependencies as Record<string, string>)?.['@mantine/core'] ||
                    'Unknown',
    pocketbaseClientVersion: packageJson.dependencies.pocketbase,
    pocketbaseServerVersion: pocketbaseServerVersion,
    pocketbaseUrl: pb.baseUrl,
  };

  return (
    <>
      <Text 
        size="sm" 
        c="dimmed" 
        style={{ cursor: 'pointer' }}
        onClick={open}
      >
        Version: {debugInfo.version}
      </Text>

      <Modal 
        opened={opened} 
        onClose={close} 
        title={<Group><IconBug size={20} />Debug Information</Group>}
        size="xl"
      >
        {loading ? (
          <Group justify="center" p="xl">
            <Text>Loading system information...</Text>
          </Group>
        ) : error ? (
          <Alert color="red" title="Error loading system information">
            {error}
          </Alert>
        ) : (
          <Tabs defaultValue="browser">
            <Tabs.List>
              <Tabs.Tab value="browser" leftSection={<IconBrowser size={14} />}>
                Browser & System
              </Tabs.Tab>
              <Tabs.Tab value="app" leftSection={<IconPackage size={14} />}>
                Application
              </Tabs.Tab>
              <Tabs.Tab value="backend" leftSection={<IconServer size={14} />}>
                Backend
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="browser" pt="md">
              <Stack>
                <Group>
                  <Text fw={500}>User Agent:</Text>
                  <Code>{systemInfo?.browser.userAgent}</Code>
                </Group>
                <Group>
                  <Text fw={500}>Platform:</Text>
                  <Code>{systemInfo?.browser.platform}</Code>
                </Group>
                <Group>
                  <Text fw={500}>Language:</Text>
                  <Code>{systemInfo?.browser.language}</Code>
                </Group>
                <Group>
                  <Text fw={500}>CPU Cores:</Text>
                  <Code>{systemInfo?.browser.hardwareConcurrency}</Code>
                </Group>
                <Group>
                  <Text fw={500}>Screen Resolution:</Text>
                  <Code>{systemInfo?.screen.width}x{systemInfo?.screen.height}</Code>
                </Group>
                <Group>
                  <Text fw={500}>Color Depth:</Text>
                  <Code>{systemInfo?.screen.colorDepth} bits</Code>
                </Group>
                <Group>
                  <Text fw={500}>Screen Orientation:</Text>
                  <Code>{systemInfo?.screen.orientation}</Code>
                </Group>
                {systemInfo?.performance?.memory && (
                  <>
                    <Group>
                      <Text fw={500}>JS Heap Size Limit:</Text>
                      <Code>{formatBytes(systemInfo.performance.memory.jsHeapSizeLimit)}</Code>
                    </Group>
                    <Group>
                      <Text fw={500}>Total JS Heap Size:</Text>
                      <Code>{formatBytes(systemInfo.performance.memory.totalJSHeapSize)}</Code>
                    </Group>
                    <Group>
                      <Text fw={500}>Used JS Heap Size:</Text>
                      <Code>{formatBytes(systemInfo.performance.memory.usedJSHeapSize)}</Code>
                    </Group>
                  </>
                )}
                <Group>
                  <Text fw={500}>Online Status:</Text>
                  <Code>{systemInfo?.browser.onLine ? 'Online' : 'Offline'}</Code>
                </Group>
                <Group>
                  <Text fw={500}>Touch Points:</Text>
                  <Code>{systemInfo?.browser.maxTouchPoints}</Code>
                </Group>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="app" pt="md">
              <Stack>
                <Group>
                  <Text fw={500}>App Version:</Text>
                  <Code>{debugInfo.version}</Code>
                </Group>
                <Group>
                  <Text fw={500}>Environment:</Text>
                  <Code>{debugInfo.nodeEnv}</Code>
                </Group>
                <Group>
                  <Text fw={500}>React Version:</Text>
                  <Code>{debugInfo.reactVersion}</Code>
                </Group>
                <Group>
                  <Text fw={500}>Mantine Version:</Text>
                  <Code>{debugInfo.mantineVersion}</Code>
                </Group>
                {systemInfo?.performance?.timing && (
                  <Group>
                    <Text fw={500}>Page Load Time:</Text>
                    <Code>
                      {(systemInfo.performance.timing.loadEventEnd - 
                        systemInfo.performance.timing.navigationStart) / 1000}s
                    </Code>
                  </Group>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="backend" pt="md">
              <Stack>
                <Group>
                  <Text fw={500}>PocketBase Server Version:</Text>
                  <Code>{debugInfo.pocketbaseServerVersion}</Code>
                </Group>
                <Group>
                  <Text fw={500}>PocketBase Client Version:</Text>
                  <Code>{debugInfo.pocketbaseClientVersion}</Code>
                </Group>
                <Group>
                  <Text fw={500}>PocketBase URL:</Text>
                  <Code>{debugInfo.pocketbaseUrl}</Code>
                </Group>
                <Group>
                  <Text fw={500}>Auth Status:</Text>
                  <Code>{pb.authStore.isValid ? 'Authenticated' : 'Not Authenticated'}</Code>
                </Group>
                <Group>
                  <Text fw={500}>Auth Model:</Text>
                  <Code>{pb.authStore.model?.id ? 'Present' : 'None'}</Code>
                </Group>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        )}
      </Modal>
    </>
  );
}
